import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';

/**
 * Page title mapping for cleaner GA reports
 */
const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/profile': 'Profile',
  '/campaigns': 'Campaigns',
  '/settings': 'Settings',
  '/getting-started': 'Getting Started',
  '/report': 'Report',
  '/report/global': 'Global Report',
  '/guides': 'Guides',
  '/guides/create': 'Create Guide',
};

/**
 * Get a human-readable page title from the path
 */
const getPageTitle = (pathname: string): string => {
  // Check exact match first
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }

  // Handle dynamic routes
  if (pathname.startsWith('/guides/edit/')) {
    return 'Edit Guide';
  }
  if (pathname.startsWith('/campaigns/')) {
    return 'Campaign Details';
  }
  if (pathname.startsWith('/report/client/')) {
    return 'Client Report';
  }

  // Fallback: convert path to title case
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'Dashboard';
  return segments
    .map(s => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' '))
    .join(' > ');
};

/**
 * Hook to automatically track page views on route changes
 * 
 * Usage: Call once in App.tsx inside a Router context
 */
export const usePageViewTracking = (): void => {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    const fullPath = `${location.pathname}${location.search}`;
    
    // Avoid tracking the same page twice (e.g., on mount + location change)
    if (previousPath.current === fullPath) {
      return;
    }
    
    previousPath.current = fullPath;
    
    // Skip tracking the auth callback page (internal redirect)
    if (location.pathname.startsWith('/auth/callback')) {
      return;
    }

    const pageTitle = getPageTitle(location.pathname);
    trackPageView(fullPath, `Videreo Portal - ${pageTitle}`);
  }, [location]);
};

export default usePageViewTracking;

